<script setup>
import {useRoute} from "vue-router";
import {onMounted, ref} from "vue";
import {getRideDetails} from "@/services/tripService";
import LoadingComponent from "@/components/utils/LoadingComponent.vue";
import BackButtonComponent from "@/components/utils/BackButtonComponent.vue";

const route = useRoute();
const id = route.params.id;
const trip = ref(null);
const loading = ref(false);

// obtener los detalles de un viaje
const fetchTrip = async () => {
  loading.value = true;
  try {
    trip.value = await getRideDetails(id);
    loading.value = false;
  } catch (err) {
    console.error(err);
    loading.value = false;
  }
};

onMounted(async () => {
  await fetchTrip();
});
</script>

<template>
  <BackButtonComponent url="/user/trips" />
  <LoadingComponent v-if="loading" />
  <div v-else>
    <div v-if="trip" class="grid grid-cols-1 lg:grid-cols-2 gap-6 pt-5">
      <!-- Trip Details -->
      <div class="bg-white dark:bg-gray-700 rounded-lg shadow-md p-6">
        <h2 class="text-2xl font-bold mb-4 text-gray-900 dark:text-gray-100">Trip Information</h2>
        <div class="space-y-4">
          <div>
            <h3 class="text-lg font-semibold text-gray-700 dark:text-gray-300">Origin</h3>
            <p class="text-gray-600 dark:text-gray-400">{{ trip.pickup.address }}</p>
            <p class="text-sm text-gray-500 dark:text-gray-500">Coordinates: {{ trip.pickup.location.latitude }}, {{ trip.pickup.location.longitude }}</p>
          </div>

          <div>
            <h3 class="text-lg font-semibold text-gray-700 dark:text-gray-300">Destination</h3>
            <p class="text-gray-600 dark:text-gray-400">{{ trip.destination.address }}</p>
            <p class="text-sm text-gray-500 dark:text-gray-500">Coordinates: {{ trip.destination.location.latitude }}, {{ trip.destination.location.longitude }}</p>
          </div>

          <div>
            <h3 class="text-lg font-semibold text-gray-700 dark:text-gray-300">Price</h3>
            <p class="text-gray-600 dark:text-gray-400">${{ trip.price }}</p>
          </div>

          <div>
            <h3 class="text-lg font-semibold text-gray-700 dark:text-gray-300">Status</h3>
            <p class="text-gray-600 dark:text-gray-400 capitalize">{{ trip.status }}</p>
          </div>

          <div>
            <h3 class="text-lg font-semibold text-gray-700 dark:text-gray-300">Payment</h3>
            <p class="text-gray-600 dark:text-gray-400">Method: {{ trip.paymentMethod }}</p>
            <p class="text-gray-600 dark:text-gray-400">Status: {{ trip.paymentStatus }}</p>
          </div>

          <div>
            <h3 class="text-lg font-semibold text-gray-700 dark:text-gray-300">Distance & Duration</h3>
            <p class="text-gray-600 dark:text-gray-400">Distance: {{ trip.distance }} miles</p>
            <p class="text-gray-600 dark:text-gray-400">Duration: {{ (trip.duration / 60).toFixed(2) }} minutes</p>
          </div>
        </div>
      </div>

      <!-- User Details -->
      <div class="bg-white dark:bg-gray-700 rounded-lg shadow-md p-6">
        <h2 class="text-2xl font-bold mb-4 text-gray-900 dark:text-gray-100">User Information</h2>
        <div class="space-y-4">
          <div v-if="trip.user">
            <div>
              <h3 class="text-lg font-semibold text-gray-700 dark:text-gray-300">Name</h3>
              <p class="text-gray-600 dark:text-gray-400">{{ trip.user.name }}</p>
            </div>

            <div>
              <h3 class="text-lg font-semibold text-gray-700 dark:text-gray-300">Email</h3>
              <p class="text-gray-600 dark:text-gray-400">{{ trip.user.email }}</p>
            </div>

            <div>
              <h3 class="text-lg font-semibold text-gray-700 dark:text-gray-300">Phone</h3>
              <p class="text-gray-600 dark:text-gray-400">{{ trip.user.phone }}</p>
            </div>
          </div>
          <div v-else>
            <p class="text-gray-500 dark:text-gray-500">No user assigned to this trip</p>
          </div>
        </div>
      </div>

      <!-- Driver Details -->
      <div class="bg-white dark:bg-gray-700 rounded-lg shadow-md p-6">
        <h2 class="text-2xl font-bold mb-4 text-gray-900 dark:text-gray-100">Driver Information</h2>
        <div class="space-y-4">
          <div v-if="trip.driver">
            <div>
              <h3 class="text-lg font-semibold text-gray-700 dark:text-gray-300">Name</h3>
              <p class="text-gray-600 dark:text-gray-400">{{ trip.driver.name }}</p>
            </div>

            <div>
              <h3 class="text-lg font-semibold text-gray-700 dark:text-gray-300">Email</h3>
              <p class="text-gray-600 dark:text-gray-400">{{ trip.driver.email }}</p>
            </div>

            <div>
              <h3 class="text-lg font-semibold text-gray-700 dark:text-gray-300">Phone</h3>
              <p class="text-gray-600 dark:text-gray-400">{{ trip.driver.phone }}</p>
            </div>

            <div>
              <h3 class="text-lg font-semibold text-gray-700 dark:text-gray-300">Vehicle</h3>
              <p class="text-gray-600 dark:text-gray-400">{{ trip.driver.vehicle }}</p>
            </div>

            <div>
              <h3 class="text-lg font-semibold text-gray-700 dark:text-gray-300">Rating</h3>
              <p class="text-gray-600 dark:text-gray-400">{{ trip.driver.rating.toFixed(1) }} / 5</p>
            </div>
          </div>
          <div v-else>
            <p class="text-gray-500 dark:text-gray-500">No driver assigned to this trip</p>
          </div>
        </div>
      </div>

      <!-- Trip Metadata -->
      <div class="bg-white dark:bg-gray-700 rounded-lg shadow-md p-6">
        <h2 class="text-2xl font-bold mb-4 text-gray-900 dark:text-gray-100">Trip Metadata</h2>
        <div class="space-y-4">
          <div>
            <h3 class="text-lg font-semibold text-gray-700 dark:text-gray-300">Created At</h3>
            <p class="text-gray-600 dark:text-gray-400">{{ new Date(trip.createdAt).toLocaleString() }}</p>
          </div>

          <div>
            <h3 class="text-lg font-semibold text-gray-700 dark:text-gray-300">Updated At</h3>
            <p class="text-gray-600 dark:text-gray-400">{{ new Date(trip.updatedAt).toLocaleString() }}</p>
          </div>

          <div>
            <h3 class="text-lg font-semibold text-gray-700 dark:text-gray-300">Completed</h3>
            <p class="text-gray-600 dark:text-gray-400">{{ trip.completedCheck ? 'Yes' : 'No' }}</p>
          </div>
        </div>
      </div>
    </div>
    <div v-else class="text-center text-gray-500 dark:text-gray-500 p-5 rounded shadow">
      No trip details available.
    </div>
  </div>
</template>

<style scoped>
/* Optional styling for mobile and desktop */
@media (min-width: 768px) {
  .grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>